import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { theme } from "../theme";
import { Box, Flex } from "./layout";
import Ripple from "./ripple";
import { IoIosArrowDown } from "react-icons/io";
import { InputWrapper } from "./input.style";
import { useOnClickOutside } from "./outside.click";
import { IoClose, IoSearch } from "react-icons/io5";

export const SelectFieldMulti = ({
  label,
  required,
  placeholder = "",
  suffix,
  prefix = "true",
  variant = "outlined",
  size = "md",
  labelBold,
  error = false,
  helperText = "",
  disabled = false,
  fullWidth = false,
  valueSinc = false,
  color = "default",
  type = "text",
  options = [],
  defaultValue,
  onChange,
  clearable = false,
  searchable = false,
  multiSelect = false, // New prop for multi-select functionality
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState(multiSelect ? [] : "");
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Handle default value
  useEffect(() => {
    if (defaultValue) {
      const defaultValues = multiSelect
        ? options.filter((opt) => defaultValue.includes(opt.id))
        : options.find((opt) => opt.id === defaultValue)?.name;

      setValue(multiSelect ? defaultValues : defaultValues || "");
    }
  }, [defaultValue, options, multiSelect]);

  // Filter options based on search term if searchable is true
  const filteredOptions = searchable
    ? options.filter((option) =>
        option.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const handleSelect = (option) => {
    if (option.disabled) return;

    if (multiSelect) {
      // Add/remove option in multi-select mode
      setValue((prev) =>
        prev.some((item) => item.id === option.id)
          ? prev.filter((item) => item.id !== option.id)
          : [...prev, option]
      );
      if (onChange) {
        const updatedValues = value.some((item) => item.id === option.id)
          ? value.filter((item) => item.id !== option.id).map((item) => item.id)
          : [...value, option].map((item) => item.id);
        onChange(updatedValues);
      }
    } else {
      setValue(option.name);
      setSearchTerm("");
      setDropdownVisible(false);
      setHighlightedIndex(-1);
      if (onChange) onChange(option.id);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setValue(multiSelect ? [] : "");
    setSearchTerm("");
    if (onChange) onChange(multiSelect ? [] : null);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!dropdownVisible) {
          setDropdownVisible(true);
        } else {
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        setDropdownVisible(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  useOnClickOutside(dropdownRef, () => {
    setDropdownVisible(false);
    setHighlightedIndex(-1);
  });

  const handleInputChange = (e) => {
    if (!searchable) return;

    setSearchTerm(e.target.value);
    if (!dropdownVisible) {
      setDropdownVisible(true);
    }
  };

  return (
    <InputWrapper
      label={label}
      variant={variant}
      error={error}
      disabled={disabled}
      mainColor={theme.colors[color]?.main || theme.colors.default.dark}
      errorColor={theme.colors.danger.main}
      isFocused={focused}
      fullWidth={fullWidth}
      valueSinc={valueSinc}
      size={size}
      suffix={suffix}
      prefix={prefix}
      labelBold={labelBold}
      dropdownVisible={dropdownVisible}
    >
      {suffix && (
        <Flex className="suffix" alignItem="center" justifyContent="center">
          {suffix}
        </Flex>
      )}
      {prefix && (
        <Flex className="prefix" alignItem="center" justifyContent="center">
          {searchable && dropdownVisible ? <IoSearch /> : <IoIosArrowDown />}
        </Flex>
      )}
      {clearable && value && (
        <Flex
          className="close"
          alignItem="center"
          justifyContent="center"
          onClick={handleClear}
        >
          <IoClose />
        </Flex>
      )}
      <input
        ref={inputRef}
        type={type}
        {...props}
        readOnly={!searchable}
        value={
          searchable && dropdownVisible
            ? searchTerm
            : multiSelect
            ? value.map((item) => item.name).join(", ")
            : value
        }
        placeholder={
          variant === "standard"
            ? placeholder
            : label
            ? focused && !value && !valueSinc
              ? placeholder
              : ""
            : !value
            ? placeholder
            : ""
        }
        disabled={disabled}
        onFocus={() => {
          setFocused(true);
          setDropdownVisible(true);
        }}
        onBlur={() => setFocused(false)}
        onClick={() => !disabled && setDropdownVisible(true)}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-expanded={dropdownVisible}
        name={label}
        id={label}
        autoComplete="off"
      />
      {label && (
        <label  htmlFor={label}
          className={`${focused || value || valueSinc ? "focused" : ""} ${
            error ? "error" : ""
          } ${disabled ? "disabled" : ""}`}
        >
          {label} {required && <Box className="required">*</Box>}
        </label>
      )}
       <fieldset
        className={`${focused || value || valueSinc ? "focused" : ""} ${
          error ? "error" : ""
        } ${!label && "noLabel"}`}
      >
        {label && (
          <legend>
            <span>
              {label}
              {required && (
                <span
                  style={{
                    color: theme.colors.danger.main,
                    paddingInlineEnd: 0,
                  }}
                />
              )}
            </span>
          </legend>
        )}
      </fieldset>
      {dropdownVisible && (
        <ul
          id="select-dropdown"
          ref={dropdownRef}
          className="dropdown"
          role="listbox"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={option.id}
                className={`dropdownItem ${
                  multiSelect && value.some((item) => item.id === option.id)
                    ? "selected"
                    : ""
                } ${
                  index === highlightedIndex ? "highlighted" : ""
                } ${option.disabled ? "disabled" : ""}`}
                onClick={() => !option.disabled && handleSelect(option)}
                role="option"
                aria-selected={
                  multiSelect
                    ? value.some((item) => item.id === option.id)
                    : value === option.name
                }
              >
                {option.name}
                <Ripple duration={1000} />
              </li>
            ))
          ) : (
            <li className="no-options" role="option" aria-disabled="true">
              No options available
            </li>
          )}
        </ul>
      )}
      {helperText && <Box className="helperText">{helperText}</Box>}
    </InputWrapper>
  );
};


SelectFieldMulti.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  variant: PropTypes.oneOf(["outlined", "filled", "standard"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  valueSinc: PropTypes.bool,
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "danger",
    "default",
  ]),
  type: PropTypes.string,
  required: PropTypes.bool,
  searchable: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
};

SelectFieldMulti.defaultProps = {
  label: "",
  placeholder: "",
  variant: "outlined",
  size: "md",
  error: false,
  helperText: "",
  disabled: false,
  fullWidth: false,
  color: "primary",
  type: "text",
  valueSinc: false,
  required: false,
  searchable: false,
  options: [],
};